import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { auth } from "../services/api";

const PROGRESS_STEPS = [
  { at: 0,  label: "Submitting your details…" },
  { at: 35, label: "Notifying the Nexora team…" },
  { at: 65, label: "Sending confirmation to your email…" },
];

const RequestAccessPage: React.FC = () => {
  const [contactName, setContactName] = useState("");
  const [societyName, setSocietyName] = useState("");
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const currentStepLabel =
    [...PROGRESS_STEPS].reverse().find((s) => progress >= s.at)?.label ?? PROGRESS_STEPS[0].label;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setProgress(5);

    let p = 5;
    intervalRef.current = setInterval(() => {
      p = p < 85 ? p + (p < 35 ? 4 : p < 65 ? 2 : 1) : p;
      setProgress(p);
    }, 180);

    try {
      await auth.requestAccess({ contactName, societyName, city: city || undefined, phone: phone || undefined, email });
      clearInterval(intervalRef.current!);
      setProgress(100);
      setTimeout(() => setSubmitted(true), 450);
    } catch (error) {
      clearInterval(intervalRef.current!);
      setIsLoading(false);
      setProgress(0);
      alert(error instanceof Error ? error.message : "Something went wrong");
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "11px 14px",
    background: "var(--nx-surface-2)",
    border: "1px solid var(--nx-border)",
    borderRadius: 10,
    fontSize: 14,
    color: "var(--nx-text-primary)",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 150ms",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--nx-navy)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        fontFamily: "var(--font-body)",
      }}
    >
      <div
        className="nx-anim-fade-up"
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--nx-surface)",
          borderRadius: 20,
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 40px 120px rgba(0,0,0,0.5)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: 3,
            background: "linear-gradient(90deg, #1A6CF6, #6D28D9)",
          }}
        />

        <div style={{ padding: "36px 32px" }}>
          {/* Logo */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 32,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                background: "linear-gradient(135deg, #1A6CF6, #6D28D9)",
                borderRadius: 9,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  fontFamily: "Syne, sans-serif",
                  fontWeight: 800,
                  fontSize: 14,
                  color: "#fff",
                }}
              >
                Nx
              </span>
            </div>
            <div>
              <div
                style={{
                  fontFamily: "Syne, sans-serif",
                  fontWeight: 800,
                  fontSize: 16,
                  color: "var(--nx-text-primary)",
                  letterSpacing: "-0.02em",
                }}
              >
                NEXORA
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "var(--nx-text-muted)",
                  letterSpacing: "0.08em",
                }}
              >
                Pentagon Analytics
              </div>
            </div>
          </div>

          {submitted ? (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <CheckCircle2
                size={48}
                style={{ color: "#10B981", marginBottom: 16 }}
              />
              <h2
                style={{
                  fontFamily: "Syne, sans-serif",
                  fontSize: 20,
                  fontWeight: 800,
                  color: "var(--nx-text-primary)",
                  marginBottom: 8,
                }}
              >
                Request submitted
              </h2>
              <p
                style={{
                  fontSize: 13,
                  color: "var(--nx-text-muted)",
                  marginBottom: 24,
                }}
              >
                Our team will review your request and send you an invite link
                within 1–2 business days.
              </p>
              <Link
                to="/login"
                state={{ requested: true }}
                style={{
                  fontSize: 13,
                  color: "var(--nx-blue)",
                  textDecoration: "none",
                }}
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 28 }}>
                <h1
                  style={{
                    fontFamily: "Syne, sans-serif",
                    fontSize: 24,
                    fontWeight: 800,
                    color: "var(--nx-text-primary)",
                    letterSpacing: "-0.03em",
                    marginBottom: 6,
                  }}
                >
                  Request access
                </h1>
                <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
                  Tell us about your community and we'll set you up.
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--nx-text-secondary)",
                      marginBottom: 6,
                      letterSpacing: "0.04em",
                    }}
                  >
                    CONTACT NAME
                  </label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="John Doe"
                    required
                    autoFocus
                    style={inputStyle}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#1A6CF6")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "var(--nx-border)")
                    }
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--nx-text-secondary)",
                      marginBottom: 6,
                      letterSpacing: "0.04em",
                    }}
                  >
                    SOCIETY NAME
                  </label>
                  <input
                    type="text"
                    value={societyName}
                    onChange={(e) => setSocietyName(e.target.value)}
                    placeholder="Green Valley Residency"
                    required
                    style={inputStyle}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#1A6CF6")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "var(--nx-border)")
                    }
                  />
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--nx-text-secondary)",
                        marginBottom: 6,
                        letterSpacing: "0.04em",
                      }}
                    >
                      CITY
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Mumbai"
                      style={inputStyle}
                      onFocus={(e) =>
                        (e.currentTarget.style.borderColor = "#1A6CF6")
                      }
                      onBlur={(e) =>
                        (e.currentTarget.style.borderColor = "var(--nx-border)")
                      }
                    />
                  </div>

                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--nx-text-secondary)",
                        marginBottom: 6,
                        letterSpacing: "0.04em",
                      }}
                    >
                      PHONE
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+91 98765 43210"
                      style={inputStyle}
                      onFocus={(e) =>
                        (e.currentTarget.style.borderColor = "#1A6CF6")
                      }
                      onBlur={(e) =>
                        (e.currentTarget.style.borderColor = "var(--nx-border)")
                      }
                    />
                  </div>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--nx-text-secondary)",
                      marginBottom: 6,
                      letterSpacing: "0.04em",
                    }}
                  >
                    WORK EMAIL
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@mycommittee.org"
                    required
                    style={inputStyle}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#1A6CF6")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "var(--nx-border)")
                    }
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="nx-btn nx-btn-primary"
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    padding: "12px",
                    fontSize: 14,
                    fontWeight: 600,
                    marginTop: 4,
                    opacity: isLoading ? 0.7 : 1,
                    cursor: isLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {isLoading ? (
                    <>
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          border: "2px solid rgba(255,255,255,0.35)",
                          borderTopColor: "#fff",
                          borderRadius: "50%",
                          display: "inline-block",
                          animation: "nx-spin 0.7s linear infinite",
                          flexShrink: 0,
                        }}
                      />
                      Sending request…
                    </>
                  ) : (
                    <>Request Access <ArrowRight size={15} /></>
                  )}
                </button>

                {isLoading && (
                  <div style={{ marginTop: 2 }}>
                    <div
                      style={{
                        height: 4,
                        background: "var(--nx-surface-2)",
                        borderRadius: 99,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${progress}%`,
                          background: "linear-gradient(90deg, #1A6CF6, #6D28D9)",
                          borderRadius: 99,
                          transition: "width 200ms ease-out",
                        }}
                      />
                    </div>
                    <p
                      style={{
                        marginTop: 6,
                        fontSize: 11,
                        color: "var(--nx-text-muted)",
                        textAlign: "center",
                      }}
                    >
                      {currentStepLabel}
                    </p>
                  </div>
                )}
              </form>

              <div
                style={{
                  marginTop: 20,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "12px 14px",
                  background:
                    "linear-gradient(135deg, rgba(26,108,246,0.06), rgba(109,40,217,0.04))",
                  border: "1px solid rgba(26,108,246,0.12)",
                  borderRadius: 10,
                }}
              >
                <Sparkles
                  size={13}
                  style={{ color: "var(--nx-blue)", flexShrink: 0 }}
                />
                <span
                  style={{ fontSize: 12, color: "var(--nx-text-secondary)" }}
                >
                  We'll send you an invite link once your community is set up.
                </span>
              </div>

              <p
                style={{
                  marginTop: 20,
                  textAlign: "center",
                  fontSize: 12,
                  color: "var(--nx-text-muted)",
                }}
              >
                Already have an account?{" "}
                <Link
                  to="/login"
                  style={{
                    color: "var(--nx-blue)",
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                >
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>

      <div
        style={{
          textAlign: "center",
          marginTop: 20,
          fontSize: 11,
          color: "var(--nx-text-muted)",
        }}
      >
        © {new Date().getFullYear()} Pentagon Analytics. All rights reserved.
      </div>
    </div>
  );
};

export default RequestAccessPage;
